import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Mail, MapPin, Phone, Send, ExternalLink } from "lucide-react";

const EMPLOYEE_PORTAL = "https://hma.snawbar.net/dashboard";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! Our team will be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const field = (k) => ({
    value: form[k],
    onChange: (e) => setForm({ ...form, [k]: e.target.value }),
  });

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="contact-page">
      <section className="py-20 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <span className="text-xs uppercase tracking-widest font-mono text-brand-400">Get in touch</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-extrabold text-slate-900">Contact & Technical Support</h1>
          <p className="mt-4 text-slate-600 max-w-2xl">
            Have a mix design challenge or need product recommendations? Send us a message and our engineers will respond.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid gap-12 lg:grid-cols-5">
        <form onSubmit={submit} className="lg:col-span-3 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs uppercase tracking-widest font-mono text-slate-500">Name *</label>
              <input {...field("name")} data-testid="contact-name" className="mt-2 w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-brand-500 outline-none" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-mono text-slate-500">Email *</label>
              <input {...field("email")} type="email" data-testid="contact-email" className="mt-2 w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-brand-500 outline-none" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-mono text-slate-500">Company</label>
            <input {...field("company")} data-testid="contact-company" className="mt-2 w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-brand-500 outline-none" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-mono text-slate-500">Message *</label>
            <textarea {...field("message")} rows={6} data-testid="contact-message" className="mt-2 w-full bg-white border border-slate-300 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-brand-500 outline-none resize-none" />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-slate-900 font-semibold transition-colors"
          >
            <Send size={17} /> {sending ? "Sending..." : "Send Message"}
          </button>
        </form>

        <div className="lg:col-span-2 space-y-4">
          {[
            { icon: MapPin, title: "Address", value: "Baghdad & Sulaymaniyah, Iraq" },
            { icon: Mail, title: "Email", value: "hma.iraq@hmachemicals.com" },
            { icon: Phone, title: "Phone", value: "+964 770 002 2222\n+964 750 333 9999\n+964 770 157 8678" },
          ].map((c) => (
            <div key={c.title} className="flex gap-4 p-5 rounded-xl bg-white border border-slate-200" data-testid={`contact-info-${c.title}`}>
              <div className="w-11 h-11 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-400">
                <c.icon size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest font-mono text-slate-500">{c.title}</div>
                <div className="mt-1 text-slate-900 font-medium whitespace-pre-line">{c.value}</div>
              </div>
            </div>
          ))}
          <a
            href={EMPLOYEE_PORTAL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-employee-portal"
            className="flex items-center justify-between p-5 rounded-xl border border-brand-500/40 bg-brand-500/10 text-brand-400 hover:bg-brand-500/20 transition-colors"
          >
            <span className="font-semibold">Employee Portal Login</span>
            <ExternalLink size={18} />
          </a>
        </div>
      </section>
    </div>
  );
}
